import {
  Alert,
  Box,
  ColumnLayout,
  Modal,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  StatusIndicatorProps,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';

import { getDripEmailPreview } from '../../common/api/dripEmails';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';

const STATUS_TYPES: Record<string, StatusIndicatorProps.Type> = {
  scheduled: 'pending',
  sent: 'in-progress',
  opened: 'info',
  clicked: 'success',
  failed: 'error',
  bounced: 'error',
  cancelled: 'stopped',
  unsubscribed: 'warning',
};

function PreviewStatus({ status }: { status: string | null | undefined }) {
  if (!status) {
    return <Box variant="p">—</Box>;
  }
  return (
    <StatusIndicator type={STATUS_TYPES[status] ?? 'pending'}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </StatusIndicator>
  );
}

export type DripEmailPreviewModalProps = {
  emailUuid: string | null;
  onDismiss: () => void;
};

/**
 * Read-only preview of a scheduled drip email body. Uses the backend preview
 * endpoint, which renders the template without sending anything or mutating
 * send status.
 */
export function DripEmailPreviewModal(props: DripEmailPreviewModalProps) {
  const { emailUuid, onDismiss } = props;

  const previewQuery = useQuery({
    queryKey: ['drip_email_preview', emailUuid],
    queryFn: async () => (emailUuid ? await getDripEmailPreview(emailUuid) : null),
    enabled: emailUuid !== null,
    staleTime: 600_000,
  });

  const data = previewQuery.data;

  return (
    <Modal
      visible={emailUuid !== null}
      onDismiss={onDismiss}
      closeAriaLabel="Close preview"
      size="large"
      header={data ? `Preview: ${data.subject}` : 'Email preview'}
    >
      {previewQuery.isLoading ? (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      ) : previewQuery.error ? (
        <Alert type="error" header="Failed to load email preview">
          {String(previewQuery.error)}
        </Alert>
      ) : data ? (
        <SpaceBetween direction="vertical" size="m">
          <Alert type="info" header="Read-only preview">
            This is a rendered preview of the scheduled email. Nothing is sent and the email
            status is not changed.
          </Alert>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">To</Box>
              <Box variant="p">{data.to_email}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <PreviewStatus status={data.status} />
            </div>
            <div>
              <Box variant="awsui-key-label">Scheduled</Box>
              <Box variant="p">
                {data.scheduled_at ? formatDateStringWithTime(data.scheduled_at) : '—'}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Campaign</Box>
              <Box variant="p">{data.campaign_name ?? '—'}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Template version</Box>
              <Box variant="p">{data.template_version ?? '—'}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Place</Box>
              <Box variant="p">{data.place_name ?? '—'}</Box>
            </div>
          </ColumnLayout>
          <div>
            <Box variant="awsui-key-label">Subject</Box>
            <Box variant="p">{data.subject}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Email body</Box>
            <iframe
              title="Scheduled email body preview"
              sandbox=""
              srcDoc={data.html}
              style={{
                width: '100%',
                height: '480px',
                border: '1px solid #e9ebed',
                borderRadius: '8px',
                background: '#ffffff',
              }}
            />
          </div>
        </SpaceBetween>
      ) : null}
    </Modal>
  );
}
